import React, { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "../../supabase/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { Link, useParams } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import "./Follows.css";
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";

export default function Follows() {
  const { translations: tRaw } = useLanguage();
  const t = tRaw?.followspage || {};
  const { userId: paramUserId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState("followers");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState({});
  const fetchingRef = useRef(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser?.id) {
      setUsers([]);
      setLoading(false);
      fetchingRef.current = false;
      return;
    }
    
    // Check if already fetching to prevent concurrent requests
    if (fetchingRef.current) return;
    
    fetchingRef.current = true;
    setLoading(true);
    
    const fetchUsers = async () => {
      
      try {
        const targetUserId = paramUserId || currentUser.id;

        let followRelations = [];
        
        if (activeTab === "followers") {
          // Fetch users who follow the target user
          const { data: followersData, error: followersError } = await supabase
            .from('follows')
            .select('follower_id')
            .eq('following_id', targetUserId);

          if (followersError) {
            console.error("Error fetching followers:", followersError);
            setUsers([]);
            setLoading(false);
            fetchingRef.current = false;
            return;
          }

          followRelations = (followersData || []).map(f => f.follower_id);
        } else {
          // Fetch users the target user is following
          const { data: followingData, error: followingError } = await supabase
            .from('follows')
            .select('following_id')
            .eq('follower_id', targetUserId);

          if (followingError) {
            console.error("Error fetching following:", followingError);
            setUsers([]);
            setLoading(false);
            fetchingRef.current = false;
            return;
          }

          followRelations = (followingData || []).map(f => f.following_id);
        }

        if (followRelations.length === 0) {
          setUsers([]);
          setLoading(false);
          fetchingRef.current = false;
          return;
        }

        // Fetch profiles for all users
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, display_name, username, avatar_url')
          .in('user_id', followRelations);

        if (profilesError) {
          console.error("Error fetching profiles:", profilesError);
          setUsers([]);
          setLoading(false);
          fetchingRef.current = false;
          return;
        }

        // Check which ones the current user is following
        const { data: currentUserFollowing, error: followingCheckError } = await supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', currentUser.id)
          .in('following_id', followRelations);

        const followingSet = new Set((currentUserFollowing || []).map(f => f.following_id));

        // Map profiles to the expected format
        const userList = (profilesData || []).map(profile => {
          // Resolve avatar URL
          let avatarUrl = '/default-avatar.png';
          if (profile.avatar_url) {
            try {
              const path = `${profile.user_id}/${profile.avatar_url}`;
              const { data } = supabase.storage.from('avatars').getPublicUrl(path);
              avatarUrl = data?.publicUrl || avatarUrl;
            } catch {
              avatarUrl = '/default-avatar.png';
            }
          }

          return {
            id: profile.user_id,
            displayName: profile.display_name || profile.username || (t?.anonymous || "Anonymous"),
            photoURL: avatarUrl,
            isFollowed: followingSet.has(profile.user_id)
          };
        });

        setUsers(userList);
      } catch (err) {
        console.error("Error fetching follows:", err);
        setUsers([]);
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchUsers();

    // Cleanup function
    return () => {
      fetchingRef.current = false;
    };
  }, [currentUser?.id, paramUserId, activeTab]);

  const toggleFollow = async (targetId, isCurrentlyFollowed) => {
    if (!currentUser?.id) return;
    
    setFollowLoading(prev => ({ ...prev, [targetId]: true }));
    
    try {
      if (isCurrentlyFollowed) {
        // Unfollow: delete the follow relationship
        const { error } = await supabase
          .from('follows')
          .delete()
          .eq('follower_id', currentUser.id)
          .eq('following_id', targetId);

        if (error) {
          console.error("Error unfollowing:", error);
          return;
        }
      } else {
        // Follow: insert a new follow relationship
        const { error } = await supabase
          .from('follows')
          .insert({
            follower_id: currentUser.id,
            following_id: targetId
          });

        if (error) {
          console.error("Error following:", error);
          return;
        }
      }

      // Update local state
      setUsers(prev =>
        prev.map(u =>
          u.id === targetId ? { ...u, isFollowed: !isCurrentlyFollowed } : u
        )
      );
    } catch (err) {
      console.error(t?.toggleError || "Failed to toggle follow", err);
    } finally {
      setFollowLoading(prev => ({ ...prev, [targetId]: false }));
    }
  };

  return (
    <div className="follows-container">
      <FloatingSidebarButton userProfile={userProfile} />
      <button
        className="pages-back-btn"
        onClick={() => navigate(-1)}
        aria-label="Back"
        title="Back"
      >
        <FiArrowLeft />
      </button>
      <div className="follows-header">
        <h2>{t?.title || "Follows"}</h2>
        <div className="tab-buttons">
          <button
            className={activeTab === "followers" ? "active" : ""}
            onClick={() => setActiveTab("followers")}
          >
            {t?.followers || "Followers"}
          </button>
          <button
            className={activeTab === "following" ? "active" : ""}
            onClick={() => setActiveTab("following")}
          >
            {t?.following || "Following"}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="follows-loading">{t?.loading || "Loading..."}</p>
      ) : users.length === 0 ? (
        <p className="follows-empty">{t?.emptyState?.[activeTab] || `No ${activeTab} yet.`}</p>
      ) : (
        <div className="follows-list">
          {users.map((user) => (
            <div key={user.id} className="follower-item">
              <Link to={`/profile/${user.id}`} className="follower-info">
                <img
                  src={user.photoURL || "/default-avatar.png"}
                  alt={user.displayName}
                  className="follower-avatar"
                />
                <span className="follower-name">
                  {user.displayName}
                </span>
              </Link>
              {user.id !== currentUser?.id && (
                <button
                  className={`follow-btn ${user.isFollowed ? "unfollow" : ""}`}
                  onClick={() => toggleFollow(user.id, user.isFollowed)}
                  disabled={followLoading[user.id]}
                >
                  {followLoading[user.id] 
                    ? "..." 
                    : user.isFollowed 
                      ? t?.unfollow || "Unfollow" 
                      : t?.follow || "Follow"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}